import { setHeaderMenuStyle } from '@theme/utilities';

/**
 * Site navigation: the glass lens that sits on the current page, magnifies its label a little and glides to the next page.
 * Markup and styles: snippets/df-nav.liquid. The bar shows on wide screens and the dropdown below COMPACT.
 *
 * The lens is two springs, one per edge. The edge it moves toward is stiffer than the one it leaves, so the glass stretches
 * while it travels and settles with one soft overshoot. Each page is a fresh load, so the position is carried over in
 * sessionStorage: a link click starts the glide on the old page and the new page carries it on from where it got to.
 */

const STORE = 'dfn:lens';
const COMPACT = '(max-width: 1139.98px)';
const LEAD = 300;
const TRAIL = 170;
const ZETA = 0.8;

const compact = window.matchMedia(COMPACT);
const motionOK = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** @returns {any} */
const load = () => {
  try {
    return JSON.parse(sessionStorage.getItem(STORE) || 'null');
  } catch (e) {
    return null;
  }
};

/** @param {any} value */
const save = (value) => {
  try {
    sessionStorage.setItem(STORE, JSON.stringify(value));
  } catch (e) {
    /* private mode: the lens just starts at the current page */
  }
};

class Glass {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.vertical = root.dataset.dfn === 'menu';
    /** @type {HTMLAnchorElement[]} */
    this.links = Array.from(root.querySelectorAll('.dfn-list:not(.dfn-mag) a.dfn-a'));
    this.current = this.links.find((a) => a.getAttribute('aria-current') === 'page') || null;
    this.key = this.current ? this.current.dataset.key || '' : '';
    this.s = 0;
    this.e = 0;
    this.vs = 0;
    this.ve = 0;
    /** @type {number[] | null} */
    this.target = null;
    this.dir = 1;
    this.raf = 0;
    this.last = 0;
    this.back = 0;

    // The page script drew the lens before first paint, where it was on the previous page.
    const from = (root.dataset.from || '').split(',').map(Number);
    if (root.hasAttribute('data-lens') && from.length === 2 && from.every(Number.isFinite)) {
      this.s = from[0];
      this.e = from[0] + from[1];
    }

    this.links.forEach((a) => a.addEventListener('click', (event) => this.onClick(event, a)));
    root.addEventListener('pointerdown', () => root.classList.add('is-press'));
    ['pointerup', 'pointercancel', 'pointerleave'].forEach((type) =>
      root.addEventListener(type, () => root.classList.remove('is-press'))
    );
    root.addEventListener('focusin', () => this.onFocus());
    root.addEventListener('focusout', () => root.removeAttribute('data-focus'));

    const track = root.querySelector('.dfn-track');
    if (track && 'ResizeObserver' in window) new ResizeObserver(() => this.sync()).observe(track);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.sync());

    window.addEventListener('pagehide', () => this.remember());
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      clearTimeout(this.back);
      this.glideTo(this.current, true);
    });

    this.glideTo(this.current);
  }

  /** The other layout is showing (the bar and the dropdown are never on screen together). */
  visible() {
    return this.links.length > 0 && this.links[0].offsetWidth > 0;
  }

  get shown() {
    return this.root.hasAttribute('data-lens');
  }

  /**
   * Where a link sits along the bar (or down the dropdown), as start and end.
   * @param {HTMLElement} a
   */
  box(a) {
    return this.vertical ? [a.offsetTop, a.offsetTop + a.offsetHeight] : [a.offsetLeft, a.offsetLeft + a.offsetWidth];
  }

  /**
   * @param {number} s
   * @param {number} e
   */
  place(s, e) {
    this.stop();
    this.s = s;
    this.e = e;
    this.paint();
  }

  paint() {
    this.root.style.setProperty('--s', `${this.s.toFixed(2)}px`);
    this.root.style.setProperty('--l', `${(this.e - this.s).toFixed(2)}px`);
  }

  stop() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.vs = 0;
    this.ve = 0;
  }

  hide() {
    this.stop();
    this.root.removeAttribute('data-lens');
  }

  /**
   * Move the lens onto a link, gliding unless told otherwise.
   * @param {HTMLAnchorElement | null} link
   * @param {boolean} [instant]
   */
  glideTo(link, instant = false) {
    if (!link) {
      this.key = '';
      this.hide();
      return;
    }
    if (!this.visible()) return;

    const [ts, te] = this.box(link);
    this.key = link.dataset.key || '';
    this.target = [ts, te];

    if (!this.shown) {
      this.place(ts, te);
      this.root.setAttribute('data-lens', '');
      return;
    }
    if (instant || !motionOK()) {
      this.place(ts, te);
      return;
    }
    if (!this.raf && Math.abs(ts - this.s) < 0.5 && Math.abs(te - this.e) < 0.5) return;

    this.dir = ts + te >= this.s + this.e ? 1 : -1;
    if (!this.raf) {
      this.last = performance.now();
      this.raf = requestAnimationFrame(this.tick);
    }
  }

  /** @param {number} now */
  tick = (now) => {
    const target = this.target;
    if (!target) return;
    const [ts, te] = target;
    let dt = Math.min(0.034, (now - this.last) / 1000);
    this.last = now;

    // two half steps keep the spring stable on a slow frame
    for (let i = 0; i < 2; i++) {
      const h = dt / 2;
      const ks = this.dir < 0 ? LEAD : TRAIL;
      const ke = this.dir > 0 ? LEAD : TRAIL;
      this.vs += ((ts - this.s) * ks - this.vs * 2 * ZETA * Math.sqrt(ks)) * h;
      this.ve += ((te - this.e) * ke - this.ve * 2 * ZETA * Math.sqrt(ke)) * h;
      this.s += this.vs * h;
      this.e += this.ve * h;
    }
    if (this.e - this.s < 12) this.e = this.s + 12;
    this.paint();

    const settled =
      Math.abs(ts - this.s) < 0.25 && Math.abs(te - this.e) < 0.25 && Math.abs(this.vs) < 3 && Math.abs(this.ve) < 3;
    if (settled) {
      this.place(ts, te);
      return;
    }
    this.raf = requestAnimationFrame(this.tick);
  };

  /** Layout moved under the lens (fonts loaded, window resized, the other layout came into view). */
  sync() {
    if (!this.visible()) return;
    if (!this.current) {
      this.hide();
      return;
    }
    const [ts, te] = this.box(this.current);
    if (!this.shown) {
      this.place(ts, te);
      this.root.setAttribute('data-lens', '');
      return;
    }
    if (this.raf) {
      this.target = [ts, te];
      return;
    }
    if (Math.abs(ts - this.s) > 0.5 || Math.abs(te - this.e) > 0.5) this.place(ts, te);
  }

  /**
   * @param {MouseEvent} event
   * @param {HTMLAnchorElement} a
   */
  onClick(event, a) {
    if (event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (a.target && a.target !== '_self') return;
    if (a === this.current) return;
    this.glideTo(a);
    this.remember();
    // if the page never changes (a cancelled load), glide back
    clearTimeout(this.back);
    this.back = window.setTimeout(() => {
      if (document.visibilityState === 'visible') this.glideTo(this.current);
    }, 3000);
  }

  onFocus() {
    const active = document.activeElement;
    if (active && active === this.current && active.matches(':focus-visible')) this.root.setAttribute('data-focus', '');
  }

  /** Leave the lens position for the next page to carry on from. Only the bar does: it is the one that stays on screen. */
  remember() {
    if (this.vertical || !this.visible()) return;
    save({
      k: this.key,
      s: this.shown ? this.s : null,
      l: this.shown ? this.e - this.s : null,
      w: window.innerWidth,
      t: Date.now(),
      v: 0,
    });
  }
}

class Menu extends Glass {
  /** @param {HTMLElement} root */
  constructor(root) {
    super(root);
    this.toggle = /** @type {HTMLButtonElement} */ (root.querySelector('.dfn-toggle'));
    this.panel = /** @type {HTMLElement} */ (root.querySelector('.dfn-panel'));
    this.scrim = /** @type {HTMLElement} */ (root.querySelector('.dfn-scrim'));
    this.hideTimer = 0;

    this.toggle.addEventListener('click', (event) => (this.isOpen ? this.close() : this.open(event.detail === 0)));
    this.scrim.addEventListener('click', () => this.close());
    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) this.close(true);
    });
    document.addEventListener('pointerdown', (event) => {
      if (this.isOpen && !root.contains(/** @type {Node} */ (event.target))) this.close();
    });
    compact.addEventListener('change', () => this.close());
  }

  get isOpen() {
    return this.root.hasAttribute('data-open');
  }

  /** @param {boolean} [keyboard] */
  open(keyboard = false) {
    clearTimeout(this.hideTimer);
    this.panel.hidden = false;
    this.scrim.hidden = false;
    this.toggle.setAttribute('aria-expanded', 'true');
    this.hide();
    void this.panel.offsetWidth; // let the closed state paint so the open transition runs
    this.glideTo(this.current, true);
    this.root.setAttribute('data-open', '');
    if (keyboard) (this.current || this.links[0]).focus();
  }

  /** @param {boolean} [returnFocus] */
  close(returnFocus = false) {
    if (!this.isOpen) return;
    this.root.removeAttribute('data-open');
    this.toggle.setAttribute('aria-expanded', 'false');
    this.hideTimer = window.setTimeout(() => {
      this.panel.hidden = true;
      this.scrim.hidden = true;
    }, 300);
    if (returnFocus) this.toggle.focus();
  }
}

const boot = () => {
  document.querySelectorAll('[data-dfn]').forEach((el) => {
    const root = /** @type {any} */ (el);
    if (root.__dfn) return;
    root.__dfn = root.dataset.dfn === 'menu' ? new Menu(root) : new Glass(root);
  });
};

boot();
document.addEventListener('shopify:section:load', boot);

// The bar needs about 1140px. Below that, and on phones, the dropdown takes over.
compact.addEventListener('change', () => setHeaderMenuStyle());
